import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { formatDateToDdmmyyyy } from './DateRangePicker';

const PANEL_WIDTH = 275;
const VIEWPORT_MARGIN = 8;

export interface DatePickerProps {
    value?: string; // YYYY-MM-DD
    onChange: (date: string) => void;
    placeholder?: string;
    className?: string;
    buttonClassName?: string;
    align?: 'left' | 'right';
    allowFutureDates?: boolean;
    maxDate?: Date;
    minDate?: Date;
    accentColor?: string; // Default: '#4a7c59' (Forest Green)
    // How many years back the year dropdown goes (default 30; 100 for a date of birth).
    yearsBack?: number;
    disabled?: boolean;
}

const MONTH_LABELS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const parseISODate = (iso?: string | null): Date | null => {
    if (!iso) return null;
    const parts = iso.split('-');
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    return new Date(y, m, d);
};

const toISODate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const isSameDay = (a: Date | null, b: Date | null): boolean => {
    if (!a || !b) return false;
    return a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();
};

const buildCalendarGrid = (viewDate: Date): (Date | null)[] => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const grid: (Date | null)[] = [];
    for (let i = 0; i < firstDay; i++) grid.push(null);
    for (let d = 1; d <= totalDays; d++) grid.push(new Date(year, month, d));
    while (grid.length % 7 !== 0) grid.push(null);
    return grid;
};

/**
 * A single-date version of DateRangePicker — same calendar UI and DD/MM/YYYY
 * display, but for one 'From Date' or 'To Date' field rather than a range.
 * Picking a day applies immediately and closes the panel, and there is no
 * clear button: this is meant for fields where a date is required, not an
 * optional filter.
 */
export const DatePicker: React.FC<DatePickerProps> = ({
    value = '',
    onChange,
    placeholder = 'Select Date',
    className = '',
    buttonClassName = '',
    align = 'right',
    allowFutureDates = false,
    maxDate,
    minDate,
    accentColor = '#4a7c59',
    yearsBack = 30,
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    // Computed fresh every open from the trigger button's actual on-screen
    // position, then clamped to the viewport — not the nearest scrollable
    // ancestor, which for a picker inside a modal's own overflow-y-auto body
    // is narrower than and offset from the viewport, so checking against the
    // viewport alone leaves the panel clipped without ever tripping a flip.
    const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);

    const today = useMemo(() => {
        const now = new Date();
        now.setHours(23, 59, 59, 999);
        return now;
    }, []);

    const effectiveMaxDate = useMemo(() => {
        if (maxDate) return maxDate;
        if (!allowFutureDates) return today;
        return undefined;
    }, [maxDate, allowFutureDates, today]);

    const selected = useMemo(() => parseISODate(value), [value]);

    const [calMonth, setCalMonth] = useState<Date>(() => selected || new Date());

    const computePosition = () => {
        const btn = buttonRef.current;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        const preferredLeft = align === 'right' ? rect.right - PANEL_WIDTH : rect.left;
        const left = Math.min(
            Math.max(preferredLeft, VIEWPORT_MARGIN),
            window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN,
        );
        setPanelPos({ top: rect.bottom + 8, left });
    };

    const handleOpen = () => {
        setCalMonth(selected || new Date());
        computePosition();
        setIsOpen(true);
    };

    const handleClose = () => setIsOpen(false);

    // Fixed positioning tracks the viewport, not the trigger, so a scroll
    // (of the page or of a modal's own scrollable body) would otherwise leave
    // the panel visually detached from the field that opened it — closing it
    // is simpler and safer than re-measuring on every scroll tick.
    useEffect(() => {
        if (!isOpen) return;
        const handleScroll = () => setIsOpen(false);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleScroll);
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            const insideButton = containerRef.current && containerRef.current.contains(target);
            const insidePanel = panelRef.current && panelRef.current.contains(target);
            if (!insideButton && !insidePanel) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const calendarGrid = useMemo(() => buildCalendarGrid(calMonth), [calMonth]);

    const canGoNextMonth = useMemo(() => {
        if (!effectiveMaxDate) return true;
        const nextMonthStart = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1);
        return nextMonthStart <= effectiveMaxDate;
    }, [calMonth, effectiveMaxDate]);

    const canGoPrevMonth = useMemo(() => {
        if (!minDate) return true;
        const prevMonthEnd = new Date(calMonth.getFullYear(), calMonth.getMonth(), 0);
        return prevMonthEnd >= minDate;
    }, [calMonth, minDate]);

    const goToMonth = (year: number, month: number) => {
        const target = new Date(year, month, 1);
        if (effectiveMaxDate) {
            const maxMonth = new Date(effectiveMaxDate.getFullYear(), effectiveMaxDate.getMonth(), 1);
            if (target > maxMonth) {
                setCalMonth(maxMonth);
                return;
            }
        }
        if (minDate) {
            const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
            if (target < minMonth) {
                setCalMonth(minMonth);
                return;
            }
        }
        setCalMonth(target);
    };

    const calendarYearOptions = useMemo(() => {
        const currentYear = today.getFullYear();
        const startYear = currentYear - yearsBack;
        const endYear = allowFutureDates ? currentYear + 5 : currentYear;
        const years: number[] = [];
        for (let y = endYear; y >= startYear; y--) {
            years.push(y);
        }
        return years;
    }, [today, allowFutureDates, yearsBack]);

    const handleDayClick = (day: Date) => {
        if (effectiveMaxDate && day > effectiveMaxDate) return;
        if (minDate && day < minDate) return;
        onChange(toISODate(day));
        setIsOpen(false);
    };

    return (
        <div className={twMerge('relative inline-block', className)} ref={containerRef}>
            <button
                ref={buttonRef}
                type="button"
                disabled={disabled}
                onClick={() => (isOpen ? handleClose() : handleOpen())}
                className={twMerge('flex items-center justify-between gap-2 h-[42px] px-3 text-xs sm:text-sm border border-slate-200 shadow-xs rounded-2xl bg-white text-slate-700 outline-none hover:border-slate-300 focus:border-[#1B4332] transition-all duration-200 cursor-pointer font-semibold whitespace-nowrap disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed', buttonClassName)}
            >
                <div className="flex items-center gap-1.5 min-w-0">
                    <Calendar size={15} className="text-slate-400 shrink-0" />
                    <span className="truncate">{value ? formatDateToDdmmyyyy(value) : placeholder}</span>
                </div>
                <ChevronDown size={13} className={`text-slate-400 transition-transform shrink-0 ml-1 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && panelPos && createPortal(
                <div
                    ref={panelRef}
                    style={{ position: 'fixed', top: panelPos.top, left: panelPos.left, width: PANEL_WIDTH }}
                    className="bg-white rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,0.12)] border border-slate-100 z-[10000] p-3 overflow-hidden font-sans"
                >
                    <div className="flex items-center justify-between gap-1 mb-2">
                        <button
                            type="button"
                            onClick={() => canGoPrevMonth && setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))}
                            disabled={!canGoPrevMonth}
                            className="p-1 hover:bg-slate-50 rounded-full transition-colors text-slate-400 shrink-0 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                            aria-label="Previous Month"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <div className="flex items-center gap-1">
                            <select
                                value={calMonth.getMonth()}
                                onChange={(e) => goToMonth(calMonth.getFullYear(), Number(e.target.value))}
                                className="font-bold text-[#2e3230] text-xs tracking-tight bg-transparent outline-none cursor-pointer appearance-none text-center"
                            >
                                {MONTH_LABELS.map((label, idx) => {
                                    const monthStart = new Date(calMonth.getFullYear(), idx, 1);
                                    const isDisabled = effectiveMaxDate ? monthStart > effectiveMaxDate : false;
                                    return (
                                        <option key={label} value={idx} disabled={isDisabled}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>
                            <select
                                value={calMonth.getFullYear()}
                                onChange={(e) => goToMonth(Number(e.target.value), calMonth.getMonth())}
                                className="font-bold text-[#2e3230] text-xs tracking-tight bg-transparent outline-none cursor-pointer appearance-none text-center"
                            >
                                {calendarYearOptions.map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="button"
                            onClick={() => canGoNextMonth && setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))}
                            disabled={!canGoNextMonth}
                            className="p-1 hover:bg-slate-50 rounded-full transition-colors text-slate-400 shrink-0 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                            aria-label="Next Month"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>

                    <div className="grid grid-cols-7 gap-x-0 mb-1">
                        {WEEKDAY_LABELS.map((d) => (
                            <div key={d} className="text-[9px] font-black text-slate-600 text-center py-0.5">
                                {d}
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-x-0 gap-y-0.5">
                        {calendarGrid.map((day, idx) => {
                            if (!day) return <div key={idx} className="h-7" />;
                            const isSelected = isSameDay(day, selected);
                            const isPastMax = effectiveMaxDate ? day > effectiveMaxDate : false;
                            const isBeforeMin = minDate ? day < minDate : false;
                            const isDisabled = isPastMax || isBeforeMin;

                            return (
                                <div key={idx} className="relative h-7 flex items-center justify-center">
                                    <button
                                        type="button"
                                        disabled={isDisabled}
                                        onClick={() => handleDayClick(day)}
                                        className={`relative z-10 w-7 h-7 flex items-center justify-center rounded-full text-[11px] font-bold transition-all cursor-pointer
                                            ${isSelected
                                                ? 'text-white shadow-sm'
                                                : isDisabled
                                                    ? 'text-slate-300 cursor-not-allowed'
                                                    : 'text-slate-600 hover:bg-slate-100'
                                            }`}
                                        style={isSelected ? { backgroundColor: accentColor } : undefined}
                                    >
                                        {day.getDate()}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default DatePicker;
